import { useMemo, useState } from 'react';
import { COUNTRIES, countryName, flagClass } from '@aiscoreboard/contracts';

interface Props {
  value: string;
  onChange: (code: string) => void;
  label?: string;
  placeholder?: string;
  showAllOnOpen?: boolean;
  autoFocusOnOpen?: boolean;
  openUp?: boolean;
}

/**
 * Search-first country picker.
 *
 * 257 flags rendered at once would be 257 requests on a venue connection, so
 * the list only appears once the player starts typing and is capped.
 */
export function CountryPicker({
  value,
  onChange,
  label = 'Country',
  placeholder = 'Search…',
  showAllOnOpen = false,
  autoFocusOnOpen = true,
  openUp = false,
}: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return showAllOnOpen ? COUNTRIES.slice(0, 300) : [];
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 40);
  }, [query, showAllOnOpen]);

  return (
    <div className="relative">
      <span className="label">{label}</span>

      {value && !open ? (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setQuery('');
          }}
          className="field flex items-center gap-2 text-left"
        >
          <span className={`${flagClass(value)} shrink-0 rounded-sm`} aria-hidden />
          <span className="truncate">{countryName(value)}</span>
          <span className="ml-auto text-white/40">change</span>
        </button>
      ) : (
        <input
          className="field"
          autoFocus={open && autoFocusOnOpen}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      )}

      {open && matches.length > 0 && (
        <ul
          className={`absolute z-30 max-h-64 w-full overflow-y-auto rounded-xl bg-hud-deep p-1 ring-1 ring-white/20 ${
            openUp ? 'bottom-full mb-1' : 'mt-1'
          }`}
        >
          {matches.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                onClick={() => {
                  onChange(c.code);
                  setQuery('');
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-white/10"
              >
                <span className={`${flagClass(c.code)} shrink-0 rounded-sm`} aria-hidden />
                <span className="truncate">{c.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A flag next to a name, used everywhere a player is listed. */
export function Flag({ code, className = '' }: { code: string; className?: string }) {
  return (
    <span
      className={`${flagClass(code)} shrink-0 rounded-sm ${className}`}
      title={countryName(code)}
      aria-label={countryName(code)}
    />
  );
}
